import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { SelectedOfferContext } from '../../../context/SelectedOfferContext';
import NavigationButtons from '../NavigationButtons';
import {
  fetchLatestContract,
  fetchRequestResumeContext,
  confirmComersRequestAfterSignature,
  setComersSignatureMethod,
  uploadComersManualSignatureFiles
} from '../../../services/comersRequestFlow';
import {
  clearStoredMobileSignatureToken,
  fetchCommerceMobileSignatureStatus,
  generateMobileSignatureLink,
  getStoredMobileSignatureToken,
  normalizeMobileSignatureUrl,
  storeMobileSignatureToken
} from '../../../services/commerceMobileSignature';
import ContractAvailabilityAlert, { CONTRACT_LOAD_STATUS } from './ContractSignature/ContractAvailabilityAlert';
import ContractPdfViewerModal from './ContractSignature/ContractPdfViewerModal';
import ContractSummaryCard from './ContractSignature/ContractSummaryCard';
import { extractContractLoadErrorMessage } from './ContractSignature/contractLoadErrorMessage';
import { MessageBlock } from '../../ui';
import {
  buildContractDownloadFilename,
  downloadContractFromUrl
} from './ContractSignature/contractSignatureDownload';
import MobileSignatureCompletedView from './ContractSignature/MobileSignatureCompletedView';
import MobileSignatureLinkPanel from './ContractSignature/MobileSignatureLinkPanel';
import PaperSignaturePanel from './ContractSignature/PaperSignaturePanel';
import SignatureMethodSelector, { SIGNATURE_METHOD_MOBILE, SIGNATURE_METHOD_PAPER } from './ContractSignature/SignatureMethodSelector';
import { SIGNATURE_STATUS } from './ContractSignature/signatureStatusConstants';
import { formatCurrency, formatDate, formatTna } from './ContractSignature/contractSignatureFormatters';
import './ContractSignatureStep.css';

const POLLING_INTERVAL_MS = 9000;
const PAPER_SIGNATURE_MAX_SIZE_BYTES = 50 * 1024 * 1024;
const PAPER_SIGNATURE_ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const PAPER_SIGNATURE_ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const IMAGE_SIGNATURE_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'];
const IMAGE_SIGNATURE_ALLOWED_TYPES = ['image/jpeg', 'image/png'];

const isValidSignatureUrl = (value = '') => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (error) {
    return false;
  }
};

const METHOD_TO_BACKEND = {
  [SIGNATURE_METHOD_MOBILE]: 'digital',
  [SIGNATURE_METHOD_PAPER]: 'manual'
};

const BACKEND_TO_METHOD = {
  digital: SIGNATURE_METHOD_MOBILE,
  manual: SIGNATURE_METHOD_PAPER
};

const MANUAL_REQUIREMENT_LABELS = {
  contractSigned: 'Contrato firmado',
  selfie: 'Selfie',
  documentFront: 'DNI frente',
  documentBack: 'DNI dorso'
};

const EMPTY_MANUAL_SIGNATURE = {
  status: 'not_started',
  contract_signed: false,
  selfie: false,
  document_front: false,
  document_back: false,
  complete: false,
  missing_requirements: ['contractSigned', 'selfie', 'documentFront', 'documentBack'],
  missing_requirement_details: [],
  can_confirm_after_signature: false
};

const normalizeManualSignature = (manualSignature = null) => {
  const normalized = {
    ...EMPTY_MANUAL_SIGNATURE,
    ...(manualSignature || {})
  };

  const missingRequirements = normalized.missing_requirements || [];
  const missingDetails = normalized.missing_requirement_details?.length
    ? normalized.missing_requirement_details
    : missingRequirements.map((alias) => ({
      alias,
      label: MANUAL_REQUIREMENT_LABELS[alias] || alias,
      message: `${MANUAL_REQUIREMENT_LABELS[alias] || alias} es obligatorio para firma manual.`
    }));

  return {
    ...normalized,
    missing_requirements: missingRequirements,
    missing_requirement_details: missingDetails
  };
};

const ContractSignatureStep = ({ onBack, onNext, onViewRequests, onClose, clientData, requestId, resumeContext = null, demoMode = false }) => {
  const { selectedOffer } = useContext(SelectedOfferContext);
  const [signatureMethod, setSignatureMethod] = useState(() => {
    if (resumeContext?.signature_method && BACKEND_TO_METHOD[resumeContext.signature_method]) {
      return BACKEND_TO_METHOD[resumeContext.signature_method];
    }
    return SIGNATURE_METHOD_MOBILE;
  });
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractUrl, setContractUrl] = useState('');
  const [contractLoadStatus, setContractLoadStatus] = useState(CONTRACT_LOAD_STATUS.IDLE);
  const [contractLoadError, setContractLoadError] = useState('');
  const [contractDownloadError, setContractDownloadError] = useState('');
  const [signatureToken, setSignatureToken] = useState('');
  const [signatureLink, setSignatureLink] = useState('');
  const [signatureExpiresAt, setSignatureExpiresAt] = useState('');
  const [signatureSignedAt, setSignatureSignedAt] = useState('');
  const [signatureApiMessage, setSignatureApiMessage] = useState('');
  const [lastSignatureStatusCheckedAt, setLastSignatureStatusCheckedAt] = useState('');
  const [signatureStatus, setSignatureStatus] = useState(SIGNATURE_STATUS.IDLE);
  const [completedSignatureMethod, setCompletedSignatureMethod] = useState('');
  const [signatureError, setSignatureError] = useState('');
  const [signatureActionMessage, setSignatureActionMessage] = useState('');
  const [signatureActionError, setSignatureActionError] = useState('');
  const [isGeneratingSignatureLink, setIsGeneratingSignatureLink] = useState(false);
  const [isSharingSignatureLink, setIsSharingSignatureLink] = useState(false);
  const [isRegeneratingSignatureLink, setIsRegeneratingSignatureLink] = useState(false);
  const [isCheckingSignatureStatus, setIsCheckingSignatureStatus] = useState(false);
  const [isUploadingPaperSignature, setIsUploadingPaperSignature] = useState(false);
  const [paperSignatureResult, setPaperSignatureResult] = useState(null);
  const [isConfirmingRequest, setIsConfirmingRequest] = useState(false);
  const [isPersistingSignatureMethod, setIsPersistingSignatureMethod] = useState(false);
  const [isDownloadingContract, setIsDownloadingContract] = useState(false);
  const [showSuccessView, setShowSuccessView] = useState(false);
  const [pendingRequirements, setPendingRequirements] = useState([]);
  const [manualSignature, setManualSignature] = useState(EMPTY_MANUAL_SIGNATURE);
  const [selectedManualFiles, setSelectedManualFiles] = useState({});
  const activeSignatureTokenRef = useRef('');
  const signatureGenerationInFlightRef = useRef(false);
  const signatureShareInFlightRef = useRef(false);
  const signatureRefreshLoadingRequestRef = useRef(0);
  const isConfirmingSignatureRegenerationRef = useRef(false);

  const firstDueDateLabel = useMemo(
    () => formatDate(selectedOffer?.firstDueDate),
    [selectedOffer?.firstDueDate]
  );

  const isContractAvailable = contractLoadStatus === CONTRACT_LOAD_STATUS.AVAILABLE && Boolean(contractUrl);

  useEffect(() => {
    activeSignatureTokenRef.current = signatureToken;
  }, [signatureToken]);

  const inferCompletedMethod = useCallback((contractState = {}) => (
    contractState.signature_type === 'physical' || contractState.signature_type === SIGNATURE_METHOD_PAPER
      ? SIGNATURE_METHOD_PAPER
      : SIGNATURE_METHOD_MOBILE
  ), []);

  const applyPostSignatureClose = useCallback(async (postSignatureClose, { showPending = false } = {}) => {
    if (!postSignatureClose) {
      return;
    }

    const requirements = postSignatureClose.pending_requirements || [];
    setPendingRequirements(showPending ? requirements : []);

    if (postSignatureClose.contract?.signed) {
      const completedMethod = inferCompletedMethod(postSignatureClose.contract);
      setSignatureStatus(SIGNATURE_STATUS.COMPLETED);
      setCompletedSignatureMethod(completedMethod);
      setSignatureMethod(completedMethod);
    }

    if (postSignatureClose.confirmed) {
      setSignatureError('');
      setShowSuccessView(true);
      await onNext?.(null, 'success');
      return;
    }

    if (showPending && requirements.length > 0) {
      setSignatureError(requirements.map((item) => item.message).filter(Boolean).join(' '));
      return;
    }

    setSignatureError('');
  }, [inferCompletedMethod, onNext]);

  const applyManualSignatureState = useCallback((manualSignaturePayload) => {
    const nextManualSignature = normalizeManualSignature(manualSignaturePayload);
    setManualSignature(nextManualSignature);
    // Los faltantes de documentación manual no son bloqueos post-firma.
    // Ese estado ya se comunica en el panel documental y en el hint de Continuar.
    setPendingRequirements([]);

    if (nextManualSignature.complete && nextManualSignature.can_confirm_after_signature) {
      setSignatureStatus(SIGNATURE_STATUS.COMPLETED);
      setCompletedSignatureMethod(SIGNATURE_METHOD_PAPER);
      setSignatureError('');
      return;
    }

    setSignatureStatus(SIGNATURE_STATUS.IDLE);
    setCompletedSignatureMethod('');
  }, []);

  useEffect(() => {
    if (!resumeContext) {
      return;
    }

    const backendMethod = resumeContext.signature_method && BACKEND_TO_METHOD[resumeContext.signature_method]
      ? BACKEND_TO_METHOD[resumeContext.signature_method]
      : '';

    if (resumeContext.signature_method && BACKEND_TO_METHOD[resumeContext.signature_method]) {
      setSignatureMethod(backendMethod);
    }

    if (resumeContext.post_signature_close?.confirmed) {
      applyPostSignatureClose(resumeContext.post_signature_close, {
        showPending: false
      });
      return;
    }

    if (backendMethod === SIGNATURE_METHOD_PAPER) {
      applyManualSignatureState(resumeContext.manual_signature);
      return;
    }

    if (resumeContext.contract_state?.signed) {
      const completedMethod = inferCompletedMethod(resumeContext.contract_state);
      setSignatureStatus(SIGNATURE_STATUS.COMPLETED);
      setCompletedSignatureMethod(completedMethod);
      setSignatureMethod(completedMethod);
    }

    if (resumeContext.post_signature_close) {
      applyPostSignatureClose(resumeContext.post_signature_close, {
        showPending: Boolean(resumeContext.contract_state?.signed)
      });
    }
  }, [applyManualSignatureState, applyPostSignatureClose, inferCompletedMethod, resumeContext]);

  const loadContract = useCallback(async () => {
    if (demoMode || !requestId) {
      return;
    }

    setContractLoadStatus(CONTRACT_LOAD_STATUS.LOADING);
    setContractLoadError('');
    setContractDownloadError('');

    try {
      const latestContract = await fetchLatestContract(requestId);

      if (latestContract?.documentUrl) {
        setContractUrl(latestContract.documentUrl);
        setContractLoadStatus(CONTRACT_LOAD_STATUS.AVAILABLE);
        return;
      }

      setContractUrl('');
      setContractLoadStatus(CONTRACT_LOAD_STATUS.UNAVAILABLE);
      setContractLoadError('No hay contrato disponible para esta solicitud.');
    } catch (error) {
      setContractUrl('');
      setContractLoadStatus(CONTRACT_LOAD_STATUS.UNAVAILABLE);
      const technicalMessage = extractContractLoadErrorMessage(error);
      setContractLoadError(
        technicalMessage || 'No se pudo recuperar el contrato actual.'
      );
    }
  }, [demoMode, requestId]);

  useEffect(() => {
    if (!demoMode && requestId) {
      loadContract();
      return undefined;
    }

    const htmlContent = `
      <html>
        <head>
          <meta charset="UTF-8" />
          <title>Contrato de Préstamo - Demo</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 32px; line-height: 1.5; color: #222; }
            h1 { margin-bottom: 8px; }
            .muted { color: #6c757d; }
            .box { border: 1px solid #d8dce1; border-radius: 8px; padding: 16px; margin-top: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            td { padding: 8px 0; border-bottom: 1px solid #eceff2; }
            td:last-child { text-align: right; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Contrato de Préstamo (Demo)</h1>
          <p class="muted">Documento de muestra para demo comercial.</p>
          <div class="box">
            <p><strong>Cliente:</strong> ${clientData?.firstName || ''} ${clientData?.lastName || ''}</p>
            <p><strong>DNI:</strong> ${clientData?.dni || 'N/A'}</p>
            <table>
              <tr><td>Capital solicitado</td><td>${formatCurrency(selectedOffer?.amount)}</td></tr>
              <tr><td>Cuotas</td><td>${selectedOffer?.term || 0}</td></tr>
              <tr><td>Valor de cuota</td><td>${formatCurrency(selectedOffer?.monthlyPayment)}</td></tr>
              <tr><td>TNA</td><td>${formatTna(selectedOffer?.interestRate)}</td></tr>
              <tr><td>Primer vencimiento</td><td>${firstDueDateLabel}</td></tr>
            </table>
          </div>
          <p style="margin-top: 40px;">Firma del cliente: __________________________</p>
          <p>Firma del comercio: _________________________</p>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    setContractUrl(url);
    setContractLoadStatus(CONTRACT_LOAD_STATUS.AVAILABLE);
    setContractLoadError('');
    setContractDownloadError('');

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [clientData?.dni, clientData?.firstName, clientData?.lastName, demoMode, firstDueDateLabel, loadContract, requestId, selectedOffer?.amount, selectedOffer?.interestRate, selectedOffer?.monthlyPayment, selectedOffer?.term]);

  const handleDownloadContract = useCallback(async () => {
    if (!contractUrl || isDownloadingContract) {
      return;
    }

    setContractDownloadError('');
    setIsDownloadingContract(true);

    try {
      const filename = buildContractDownloadFilename({
        requestId,
        dni: clientData?.dni,
        demoMode
      });

      await downloadContractFromUrl(contractUrl, filename);
    } catch (error) {
      setContractDownloadError('No se pudo descargar el contrato.');
    } finally {
      setIsDownloadingContract(false);
    }
  }, [clientData?.dni, contractUrl, demoMode, isDownloadingContract, requestId]);

  const setSignatureActionFeedback = useCallback(({ message = '', error = '' } = {}) => {
    setSignatureActionMessage((current) => (current === message ? current : message));
    setSignatureActionError((current) => (current === error ? current : error));
  }, []);

  const clearSignatureActionFeedback = useCallback(() => {
    setSignatureActionFeedback();
  }, [setSignatureActionFeedback]);

  const handleCopySignatureLink = useCallback(async () => {
    if (!isValidSignatureUrl(signatureLink)) return;

    try {
      clearSignatureActionFeedback();
      const writeText = typeof navigator !== 'undefined' ? navigator.clipboard?.writeText : null;
      if (typeof writeText !== 'function') {
        throw new Error('Clipboard API unavailable');
      }

      await writeText.call(navigator.clipboard, signatureLink);
      setSignatureActionFeedback({ message: 'Enlace copiado' });
    } catch (error) {
      setSignatureActionFeedback({
        error: 'No pudimos copiar el enlace. Seleccionalo y copialo manualmente.'
      });
    }
  }, [clearSignatureActionFeedback, setSignatureActionFeedback, signatureLink]);

  const handleShareSignatureLink = useCallback(async () => {
    if (!isValidSignatureUrl(signatureLink) || signatureShareInFlightRef.current) return;

    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
      return;
    }

    signatureShareInFlightRef.current = true;

    try {
      clearSignatureActionFeedback();
      setIsSharingSignatureLink(true);
      await navigator.share({
        title: 'Firma de contrato',
        text: 'Abrí este enlace para revisar y firmar el contrato.',
        url: signatureLink
      });
      setSignatureActionFeedback({ message: 'Enlace compartido' });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        setSignatureActionFeedback({
          error: 'No pudimos compartir el enlace. Podés copiarlo e intentarlo desde otra aplicación.'
        });
      }
    } finally {
      signatureShareInFlightRef.current = false;
      setIsSharingSignatureLink(false);
    }
  }, [
    clearSignatureActionFeedback,
    setSignatureActionFeedback,
    signatureLink
  ]);

  const handleOpenSignatureOnDevice = useCallback(() => {
    if (!isValidSignatureUrl(signatureLink)) return;
    window.open(signatureLink, '_blank', 'noopener,noreferrer');
  }, [signatureLink]);

  const applySignatureStatus = useCallback((statusResponse) => {
    const nextStatus = statusResponse?.status || SIGNATURE_STATUS.ERROR;
    setSignatureStatus(nextStatus);

    if (statusResponse?.link?.expires_at) {
      setSignatureExpiresAt(statusResponse.link.expires_at);
    }

    if (statusResponse?.contract?.signed_at) {
      setSignatureSignedAt(statusResponse.contract.signed_at);
    }

    if (nextStatus === SIGNATURE_STATUS.COMPLETED) {
      setCompletedSignatureMethod(SIGNATURE_METHOD_MOBILE);
    }

    if (nextStatus === SIGNATURE_STATUS.NOT_SIGNABLE) {
      setSignatureApiMessage(statusResponse?.message || '');
      setSignatureError(statusResponse?.message || 'No se pudo consultar el estado de firma.');
      return;
    }

    if (nextStatus === SIGNATURE_STATUS.ERROR) {
      setSignatureApiMessage('');
      setSignatureError(statusResponse?.message || 'No se pudo consultar el estado de firma.');
      return;
    }

    setSignatureApiMessage('');
    setSignatureError('');
  }, []);

  const recordSuccessfulSignatureStatusCheck = useCallback((statusResponse) => {
    applySignatureStatus(statusResponse);
    setLastSignatureStatusCheckedAt(new Date().toISOString());
  }, [applySignatureStatus]);

  const handleRefreshSignatureStatus = useCallback(async ({ token, silent = false } = {}) => {
    const tokenToCheck = token || signatureToken;

    if (!tokenToCheck) {
      return;
    }

    const refreshRequestId = silent ? 0 : signatureRefreshLoadingRequestRef.current + 1;

    try {
      if (!silent) {
        signatureRefreshLoadingRequestRef.current = refreshRequestId;
        setIsCheckingSignatureStatus(true);
      }

      const statusResponse = await fetchCommerceMobileSignatureStatus(tokenToCheck);
      if (activeSignatureTokenRef.current !== tokenToCheck) {
        return;
      }

      recordSuccessfulSignatureStatusCheck(statusResponse);

      if (statusResponse?.status === SIGNATURE_STATUS.COMPLETED && requestId) {
        try {
          const context = await fetchRequestResumeContext(requestId);
          await applyPostSignatureClose(context.post_signature_close, {
            showPending: Boolean(context.contract_state?.signed)
          });
        } catch (refreshError) {
          // Keep the validated signature state even if resume context refresh fails.
        }
      }
    } catch (error) {
      if (activeSignatureTokenRef.current !== tokenToCheck) {
        return;
      }

      setSignatureStatus(SIGNATURE_STATUS.ERROR);
      setSignatureError(error.response?.data?.message || 'No se pudo consultar el estado de firma.');
    } finally {
      if (
        !silent
        && signatureRefreshLoadingRequestRef.current === refreshRequestId
        && activeSignatureTokenRef.current === tokenToCheck
      ) {
        setIsCheckingSignatureStatus(false);
      }
    }
  }, [applyPostSignatureClose, recordSuccessfulSignatureStatusCheck, requestId, signatureToken]);

  const handleManualRefreshSignatureStatus = useCallback(() => {
    handleRefreshSignatureStatus({ silent: false });
  }, [handleRefreshSignatureStatus]);

  const handleGenerateSignatureLink = useCallback(async ({ regenerate = false, persistMethod = false } = {}) => {
    if (signatureGenerationInFlightRef.current || isGeneratingSignatureLink) {
      return;
    }

    if (demoMode) {
      setSignatureStatus(SIGNATURE_STATUS.ERROR);
      setSignatureError('La firma mobile requiere una solicitud real.');
      setCompletedSignatureMethod('');
      return;
    }

    if (!requestId) {
      setSignatureStatus(SIGNATURE_STATUS.ERROR);
      setSignatureError('No se encontró la solicitud para generar el enlace de firma.');
      setCompletedSignatureMethod('');
      return;
    }

    if (!isContractAvailable) {
      return;
    }

    if (
      !regenerate
      && signatureStatus === SIGNATURE_STATUS.COMPLETED
      && completedSignatureMethod === SIGNATURE_METHOD_MOBILE
    ) {
      return;
    }

    signatureGenerationInFlightRef.current = true;

    try {
      setIsGeneratingSignatureLink(true);
      setIsRegeneratingSignatureLink(regenerate);
      clearSignatureActionFeedback();

      if (!regenerate) {
        setSignatureStatus(SIGNATURE_STATUS.GENERATING);
      }

      setSignatureError('');

      if (!regenerate) {
        setCompletedSignatureMethod('');
      }

      if (persistMethod) {
        await setComersSignatureMethod(requestId, METHOD_TO_BACKEND[SIGNATURE_METHOD_MOBILE]);
      }

      if (!regenerate) {
        const storedToken = getStoredMobileSignatureToken(requestId);
        if (storedToken) {
          const statusResponse = await fetchCommerceMobileSignatureStatus(storedToken);
          const restoredStatus = statusResponse?.status || SIGNATURE_STATUS.ERROR;
          const restoredLink = normalizeMobileSignatureUrl({ token: storedToken });

          activeSignatureTokenRef.current = storedToken;
          signatureRefreshLoadingRequestRef.current += 1;
          setIsCheckingSignatureStatus(false);
          setSignatureToken(storedToken);
          setSignatureLink(restoredLink);
          setSignatureExpiresAt(statusResponse?.link?.expires_at || '');
          recordSuccessfulSignatureStatusCheck(statusResponse);

          if (restoredStatus === SIGNATURE_STATUS.COMPLETED) {
            setCompletedSignatureMethod(SIGNATURE_METHOD_MOBILE);
            if (requestId) {
              const context = await fetchRequestResumeContext(requestId);
              await applyPostSignatureClose(context.post_signature_close, {
                showPending: Boolean(context.contract_state?.signed)
              });
            }
            return;
          }

          if (
            restoredStatus === SIGNATURE_STATUS.PENDING
            || restoredStatus === SIGNATURE_STATUS.EXPIRED
            || restoredStatus === SIGNATURE_STATUS.SUPERSEDED
          ) {
            return;
          }

          clearStoredMobileSignatureToken(requestId);
        }
      }

      const linkResponse = await generateMobileSignatureLink(requestId);
      const nextToken = linkResponse.token || '';
      const nextLink = linkResponse.mobile_url || '';

      if (!nextToken || !isValidSignatureUrl(nextLink)) {
        throw new Error('No se recibió un enlace de firma válido.');
      }

      activeSignatureTokenRef.current = nextToken;
      signatureRefreshLoadingRequestRef.current += 1;
      setIsCheckingSignatureStatus(false);
      setSignatureToken(nextToken);
      setSignatureLink(nextLink);
      setSignatureExpiresAt(linkResponse.expires_at || '');
      setSignatureSignedAt('');
      setSignatureApiMessage('');
      setLastSignatureStatusCheckedAt('');
      setSignatureActionFeedback(
        regenerate ? { message: 'Enlace nuevo generado' } : {}
      );
      setSignatureStatus(linkResponse.status || SIGNATURE_STATUS.PENDING);
      setCompletedSignatureMethod('');
      storeMobileSignatureToken(requestId, nextToken);

      await handleRefreshSignatureStatus({ token: nextToken, silent: true });
    } catch (error) {
      if (regenerate) {
        setSignatureActionFeedback({
          error: 'No pudimos generar un enlace nuevo. El enlace anterior sigue disponible.'
        });
      } else {
        setSignatureStatus(SIGNATURE_STATUS.ERROR);
        setCompletedSignatureMethod('');
        setSignatureError(error.response?.data?.message || 'No se pudo generar el enlace de firma.');
      }
    } finally {
      signatureGenerationInFlightRef.current = false;
      setIsGeneratingSignatureLink(false);
      setIsRegeneratingSignatureLink(false);
    }
  }, [
    applyPostSignatureClose,
    clearSignatureActionFeedback,
    completedSignatureMethod,
    demoMode,
    handleRefreshSignatureStatus,
    isContractAvailable,
    isGeneratingSignatureLink,
    recordSuccessfulSignatureStatusCheck,
    requestId,
    setSignatureActionFeedback,
    signatureStatus
  ]);

  const handleRegenerateSignatureLink = useCallback(async () => {
    if (
      isGeneratingSignatureLink
      || isRegeneratingSignatureLink
      || isConfirmingSignatureRegenerationRef.current
    ) {
      return;
    }

    isConfirmingSignatureRegenerationRef.current = true;
    const triggerElement = document.activeElement;
    try {
      const result = await Swal.fire({
        title: '¿Generar un enlace nuevo?',
        html: `
          <p>Se generará otro enlace para la firma. El enlace anterior podría continuar activo hasta su vencimiento.</p>
          <p>Compartí el enlace nuevo con el cliente para evitar confusiones.</p>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Generar nuevo enlace',
        cancelButtonText: 'Cancelar',
        reverseButtons: true
      });

      if (typeof triggerElement?.focus === 'function') {
        triggerElement.focus();
      }

      if (!result.isConfirmed) {
        return;
      }

      await handleGenerateSignatureLink({ regenerate: true, persistMethod: false });
    } finally {
      isConfirmingSignatureRegenerationRef.current = false;
    }
  }, [handleGenerateSignatureLink, isGeneratingSignatureLink, isRegeneratingSignatureLink]);

  useEffect(() => {
    if (demoMode || !requestId) {
      return;
    }

    if (signatureMethod !== SIGNATURE_METHOD_MOBILE) {
      return;
    }

    if (!isContractAvailable) {
      return;
    }

    if (
      completedSignatureMethod === SIGNATURE_METHOD_MOBILE
      && signatureStatus === SIGNATURE_STATUS.COMPLETED
    ) {
      return;
    }

    if (isGeneratingSignatureLink) {
      return;
    }

    if (signatureStatus === SIGNATURE_STATUS.EXPIRED || signatureStatus === SIGNATURE_STATUS.SUPERSEDED) {
      return;
    }

    if (signatureStatus === SIGNATURE_STATUS.ERROR) {
      return;
    }

    if (signatureLink && signatureStatus === SIGNATURE_STATUS.PENDING) {
      return;
    }

    if (signatureStatus === SIGNATURE_STATUS.GENERATING) {
      return;
    }

    handleGenerateSignatureLink({
      regenerate: false,
      persistMethod: true
    });
  }, [
    completedSignatureMethod,
    demoMode,
    handleGenerateSignatureLink,
    isContractAvailable,
    isGeneratingSignatureLink,
    requestId,
    signatureLink,
    signatureMethod,
    signatureStatus
  ]);

  useEffect(() => {
    if (signatureMethod !== SIGNATURE_METHOD_MOBILE || signatureStatus !== SIGNATURE_STATUS.PENDING || !signatureToken) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      handleRefreshSignatureStatus({ token: signatureToken, silent: true });
    }, POLLING_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [handleRefreshSignatureStatus, signatureMethod, signatureStatus, signatureToken]);

  const handleSignatureMethodChange = useCallback(async (nextMethod) => {
    setSignatureMethod(nextMethod);
    setSignatureError('');
    clearSignatureActionFeedback();
    setPendingRequirements([]);

    if (!demoMode && requestId) {
      try {
        setIsPersistingSignatureMethod(true);
        await setComersSignatureMethod(requestId, METHOD_TO_BACKEND[nextMethod]);
      } catch (error) {
        setSignatureError(
          error.response?.data?.error?.message
            || error.response?.data?.message
            || 'No se pudo guardar la modalidad de firma.'
        );
      } finally {
        setIsPersistingSignatureMethod(false);
      }
    }

    if (nextMethod === SIGNATURE_METHOD_MOBILE) {
      if (completedSignatureMethod === SIGNATURE_METHOD_MOBILE) {
        setSignatureStatus(SIGNATURE_STATUS.COMPLETED);
        return;
      }

      setSignatureStatus(signatureToken ? SIGNATURE_STATUS.PENDING : SIGNATURE_STATUS.IDLE);
      return;
    }

    setSignatureStatus(manualSignature.complete && manualSignature.can_confirm_after_signature
      ? SIGNATURE_STATUS.COMPLETED
      : SIGNATURE_STATUS.IDLE);
    setCompletedSignatureMethod(manualSignature.complete && manualSignature.can_confirm_after_signature
      ? SIGNATURE_METHOD_PAPER
      : '');
  }, [clearSignatureActionFeedback, completedSignatureMethod, demoMode, manualSignature, requestId, signatureToken]);

  const handleClearPaperSignatureError = useCallback(() => {
    if (signatureMethod === SIGNATURE_METHOD_PAPER) {
      setSignatureError('');
      if (!manualSignature.complete) {
        setSignatureStatus(SIGNATURE_STATUS.IDLE);
      }
    }
  }, [manualSignature.complete, signatureMethod]);

  const validatePaperSignatureFile = useCallback((file, alias = 'contractSigned') => {
    if (!file) {
      return 'Seleccioná todos los archivos requeridos para firma manual.';
    }

    const extension = file.name?.split('.').pop()?.toLowerCase() || '';
    const isIdentityImage = alias === 'selfie';
    const allowedExtensions = isIdentityImage ? IMAGE_SIGNATURE_ALLOWED_EXTENSIONS : PAPER_SIGNATURE_ALLOWED_EXTENSIONS;
    const allowedTypes = isIdentityImage ? IMAGE_SIGNATURE_ALLOWED_TYPES : PAPER_SIGNATURE_ALLOWED_TYPES;
    const hasAllowedExtension = allowedExtensions.includes(extension);
    const hasAllowedType = !file.type || allowedTypes.includes(file.type);

    if (!hasAllowedExtension || !hasAllowedType) {
      return isIdentityImage
        ? 'La selfie debe ser JPG o PNG.'
        : 'El archivo debe ser PDF, JPG o PNG.';
    }

    if (file.size > PAPER_SIGNATURE_MAX_SIZE_BYTES) {
      return 'El archivo no debe superar los 50MB.';
    }

    return '';
  }, []);

  const handlePaperSignatureUpload = useCallback(async (filesByAlias) => {
    if (isUploadingPaperSignature) {
      return;
    }

    const requiredAliases = ['contractSigned', 'selfie', 'documentFront', 'documentBack'];
    const selectedAliases = requiredAliases.filter((alias) => filesByAlias?.[alias]);

    if (selectedAliases.length === 0) {
      setSignatureStatus(SIGNATURE_STATUS.ERROR);
      setSignatureError('Seleccioná al menos un archivo para firma manual.');
      return;
    }

    const validationError = selectedAliases
      .map((alias) => validatePaperSignatureFile(filesByAlias?.[alias], alias))
      .find(Boolean);

    if (validationError) {
      setSignatureStatus(SIGNATURE_STATUS.ERROR);
      setSignatureError(validationError);
      setCompletedSignatureMethod('');
      return;
    }

    if (demoMode) {
      setSignatureStatus(SIGNATURE_STATUS.ERROR);
      setSignatureError('La firma en papel requiere una solicitud real.');
      setCompletedSignatureMethod('');
      return;
    }

    if (!requestId) {
      setSignatureStatus(SIGNATURE_STATUS.ERROR);
      setSignatureError('No se encontró la solicitud para subir el contrato firmado.');
      setCompletedSignatureMethod('');
      return;
    }

    try {
      setIsUploadingPaperSignature(true);
      setSignatureError('');
      setPaperSignatureResult(null);

      const result = await uploadComersManualSignatureFiles(requestId, filesByAlias);
      const nextManualSignature = normalizeManualSignature(result.manual_signature);

      setPaperSignatureResult({
        ...result,
        fileNames: selectedAliases
          .map((alias) => filesByAlias?.[alias]?.name)
          .filter(Boolean)
      });
      applyManualSignatureState(nextManualSignature);
      setSelectedManualFiles({});
    } catch (error) {
      if (error.response?.status === 409 && requestId) {
        try {
          const context = await fetchRequestResumeContext(requestId);
          applyManualSignatureState(context.manual_signature);
          setSignatureError('');
          return;
        } catch (refreshError) {
          // Keep the original upload error visible when refresh also fails.
        }
      }

      setSignatureStatus(SIGNATURE_STATUS.ERROR);
      setSignatureError(
        error.response?.data?.error?.message
          || error.response?.data?.message
          || 'No se pudo subir el contrato firmado.'
      );
    } finally {
      setIsUploadingPaperSignature(false);
    }
  }, [applyManualSignatureState, demoMode, isUploadingPaperSignature, requestId, validatePaperSignatureFile]);

  const handleManualFileSelect = useCallback((alias, file) => {
    setSelectedManualFiles((current) => ({
      ...current,
      [alias]: file
    }));
    handleClearPaperSignatureError();
  }, [handleClearPaperSignatureError]);

  const handleSaveManualDocumentation = useCallback(() => {
    handlePaperSignatureUpload(selectedManualFiles);
  }, [handlePaperSignatureUpload, selectedManualFiles]);

  const canContinue = signatureStatus === SIGNATURE_STATUS.COMPLETED
    && completedSignatureMethod === signatureMethod
    && !isConfirmingRequest
    && !isPersistingSignatureMethod;

  const continueHint = useMemo(() => {
    if (canContinue || isConfirmingRequest || isPersistingSignatureMethod) {
      return '';
    }

    if (
      signatureMethod === SIGNATURE_METHOD_MOBILE
      && completedSignatureMethod !== SIGNATURE_METHOD_MOBILE
    ) {
      return 'Se habilitará automáticamente cuando validemos la firma del cliente.';
    }

    return '';
  }, [
    canContinue,
    completedSignatureMethod,
    isConfirmingRequest,
    isPersistingSignatureMethod,
    signatureMethod
  ]);

  const handleContinue = useCallback(async () => {
    if (!canContinue) {
      return;
    }

    if (demoMode) {
      setSignatureError('La confirmación requiere una solicitud real.');
      return;
    }

    if (!requestId) {
      setSignatureError('No se encontró la solicitud para confirmar.');
      return;
    }

    try {
      setIsConfirmingRequest(true);
      setSignatureError('');

      await confirmComersRequestAfterSignature(requestId);

      setShowSuccessView(true);
      await onNext?.(null, 'success');
    } catch (error) {
      const manualSignatureDetails = error.response?.data?.error?.details?.manual_signature;
      if (manualSignatureDetails) {
        applyManualSignatureState(manualSignatureDetails);
      }

      if (error.response?.status === 409 && requestId) {
        try {
          const context = await fetchRequestResumeContext(requestId);
          if (context.manual_signature) {
            applyManualSignatureState(context.manual_signature);
          }
        } catch (refreshError) {
          // Keep the original confirmation error visible when refresh also fails.
        }
      }

      setSignatureError(
        error.response?.data?.error?.message
          || error.response?.data?.message
          || 'No se pudo enviar la solicitud a análisis de pago.'
      );
    } finally {
      setIsConfirmingRequest(false);
    }
  }, [applyManualSignatureState, canContinue, demoMode, onNext, requestId]);

  const handleFinish = useCallback(async () => {
    await onNext?.(null, 'success');

    if (typeof onViewRequests === 'function') {
      onViewRequests();
      return;
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose, onNext, onViewRequests]);

  if (showSuccessView) {
    return (
      <>
        <MobileSignatureCompletedView
          clientData={clientData}
          selectedOffer={selectedOffer}
          onViewContract={() => setShowContractModal(true)}
          onDownloadContract={handleDownloadContract}
          onBack={onBack}
          onFinish={handleFinish}
        />

        <ContractPdfViewerModal
          show={showContractModal}
          onHide={() => setShowContractModal(false)}
          title="Contrato de préstamo"
          url={contractUrl}
        />
      </>
    );
  }

  return (
    <>
      <div className="container rg-request-confirmation-step">
        <h4 className="text-center rg-request-step__title">Firma y confirmación</h4>

        <ContractAvailabilityAlert
          contractLoadStatus={contractLoadStatus}
          contractLoadError={contractLoadError}
          contractDownloadError={contractDownloadError}
          isDownloadingContract={isDownloadingContract}
          onRetryLoad={loadContract}
          onRetryDownload={handleDownloadContract}
          onViewContract={() => setShowContractModal(true)}
        />

        {pendingRequirements.length > 0 && signatureStatus === SIGNATURE_STATUS.COMPLETED && (
          <div className="alert alert-warning mb-4">
            <div className="fw-semibold mb-2">La firma quedó registrada, pero todavía no se pudo enviar a análisis de pago.</div>
            <ul className="mb-0 ps-3">
              {pendingRequirements.map((requirement, index) => (
                <li key={`${requirement.code || 'pending'}-${index}`}>
                  {requirement.message || 'Hay un requisito pendiente para continuar.'}
                </li>
              ))}
            </ul>
          </div>
        )}

        {isPersistingSignatureMethod && (
          <div className="alert alert-light border mb-4">
            Guardando modalidad de firma...
          </div>
        )}

        {isDownloadingContract && (
          <div className="alert alert-light border mb-4">
            Descargando contrato...
          </div>
        )}

        <ContractSummaryCard
          selectedOffer={selectedOffer}
          firstDueDateLabel={firstDueDateLabel}
          contractUrl={contractUrl}
          onViewContract={() => setShowContractModal(true)}
          onDownloadContract={handleDownloadContract}
        />

        <section className="rg-signature-decision" aria-labelledby="signature-method-title">
          <div className="rg-signature-decision__header">
            <h5 id="signature-method-title" className="rg-request-section__title">
              ¿Cómo firmará el cliente?
            </h5>
          </div>

          <SignatureMethodSelector
            selectedMethod={signatureMethod}
            onSelectMethod={handleSignatureMethodChange}
            isDigitalSignatureAvailable={isContractAvailable}
            isPersistingSelection={isPersistingSignatureMethod}
            groupLabelId="signature-method-title"
          />

          <div className="rg-signature-context-panel" aria-live="polite">
            {signatureMethod === SIGNATURE_METHOD_MOBILE ? (
              isContractAvailable ? (
                <MobileSignatureLinkPanel
                  signatureStatus={signatureStatus}
                  signatureLink={signatureLink}
                  signatureExpiresAt={signatureExpiresAt}
                  lastSignatureStatusCheckedAt={lastSignatureStatusCheckedAt}
                  signatureSignedAt={signatureSignedAt}
                  signatureApiMessage={signatureApiMessage}
                  signatureError={signatureError}
                  signatureActionMessage={signatureActionMessage}
                  signatureActionError={signatureActionError}
                  isGeneratingLink={isGeneratingSignatureLink}
                  isRegeneratingLink={isRegeneratingSignatureLink}
                  isSharingLink={isSharingSignatureLink}
                  isRefreshingSignature={isCheckingSignatureStatus}
                  canRefreshSignatureStatus={Boolean(signatureToken)}
                  onCopyLink={handleCopySignatureLink}
                  onShareLink={handleShareSignatureLink}
                  onOpenOnDevice={handleOpenSignatureOnDevice}
                  onRegenerateLink={handleRegenerateSignatureLink}
                  onRefreshStatus={handleManualRefreshSignatureStatus}
                />
              ) : (
                <MessageBlock
                  tone="warning"
                  title="Firma digital no disponible"
                  className="rg-signature-panel rg-signature-panel--blocked"
                >
                  {contractLoadStatus === CONTRACT_LOAD_STATUS.LOADING
                    ? 'Esperá a que carguemos el contrato para generar el enlace de firma.'
                    : 'La firma digital no puede iniciarse hasta que el contrato esté disponible.'}
                </MessageBlock>
              )
            ) : (
              <PaperSignaturePanel
                signatureError={signatureMethod === SIGNATURE_METHOD_PAPER ? signatureError : ''}
                isUploading={isUploadingPaperSignature}
                manualSignature={manualSignature}
                selectedFiles={selectedManualFiles}
                onFileSelect={handleManualFileSelect}
                onUpload={handleSaveManualDocumentation}
              />
            )}
          </div>
        </section>

        <NavigationButtons
          onBack={onBack}
          onNext={handleContinue}
          nextLabel="Continuar a confirmación"
          nextHint={continueHint}
          isLoading={isConfirmingRequest}
          disableNext={!canContinue}
        />
      </div>

      <ContractPdfViewerModal
        show={showContractModal}
        onHide={() => setShowContractModal(false)}
        title="Contrato de préstamo"
        url={contractUrl}
      />
    </>
  );
};

export default ContractSignatureStep;
